import { useLanguage } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { Link } from "react-router-dom";
import { ArrowRight, User, Users } from "lucide-react";
import { WhatsAppIcon } from "../components/WhatsAppIcon";
import { waLink, PHONE_DISPLAY } from "../lib/spa";
import { PaymentMethods } from "../components/PaymentMethods";

export default function Precos() {
  const { t } = useLanguage();

  return (
    <div data-testid="prices-page" className="mx-auto max-w-6xl px-5 pb-24 pt-32 sm:px-8 sm:pt-40">
      <Seo title={`${t("prices.title")} | Magnolia Lunar`} description={t("prices.subtitle")} />

      <Reveal>
        <p className="text-[10px] uppercase tracking-[0.45em] text-gold">{t("prices.label")}</p>
        <h1 className="mt-3 font-display text-4xl font-light text-foreground sm:text-5xl lg:text-6xl">
          {t("prices.title")}
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground/72">{t("prices.subtitle")}</p>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {/* Individual */}
        <Reveal delay={0.1}>
          <div
            data-testid="price-card-individual"
            className="group relative overflow-hidden rounded-2xl border border-foreground/8 bg-card p-10 transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:shadow-[0_20px_60px_-15px_rgba(212,175,55,0.25)]"
          >
            <User className="h-8 w-8 text-gold" strokeWidth={1.25} />
            <h2 className="mt-6 font-display text-2xl font-light text-foreground">{t("prices.individualTitle")}</h2>
            <div className="mt-8 flex items-baseline gap-2">
              <span className="text-xs uppercase tracking-[0.3em] text-foreground/55">{t("prices.individualFrom")}</span>
              <span data-testid="price-individual-value" className="font-display text-6xl font-light text-gold">
                {t("prices.individualPrice")}
              </span>
            </div>
          </div>
        </Reveal>

        {/* Couples */}
        <Reveal delay={0.2}>
          <div
            data-testid="price-card-couple"
            className="group relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-gold/10 via-transparent to-transparent p-10 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_60px_-15px_rgba(212,175,55,0.3)]"
          >
            <Users className="h-8 w-8 text-gold" strokeWidth={1.25} />
            <h2 className="mt-6 font-display text-2xl font-light text-foreground">{t("prices.coupleTitle")}</h2>
            <div className="mt-8 flex items-baseline gap-2">
              <span className="text-xs uppercase tracking-[0.3em] text-foreground/55">{t("prices.coupleFrom")}</span>
              <span data-testid="price-couple-value" className="font-display text-6xl font-light text-gold">
                {t("prices.couplePrice")}
              </span>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Note + CTA */}
      <Reveal delay={0.3}>
        <div className="mt-12 rounded-2xl border border-foreground/8 bg-card p-8 sm:p-10">
          <p className="text-sm leading-relaxed text-foreground/72">{t("prices.note")}</p>

          <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <a
              href={waLink(t("booking.generalMessage"))}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="prices-whatsapp-cta"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:bg-gold-light"
            >
              <WhatsAppIcon className="h-4 w-4" />
              {t("prices.ctaWhatsapp")}
            </a>
            <span className="text-sm text-foreground/60">{PHONE_DISPLAY}</span>
          </div>

          <Link
            to="/massagens"
            data-testid="prices-view-massages"
            className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold transition-colors hover:text-gold-light"
          >
            {t("prices.viewMassages")}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>

      {/* Payment methods */}
      <Reveal delay={0.4}>
        <div className="mt-10">
          <h3 className="text-xs uppercase tracking-[0.3em] text-foreground/55">{t("prices.paymentsTitle")}</h3>
          <PaymentMethods className="mt-3" />
        </div>
      </Reveal>
    </div>
  );
}
